import * as React from 'react'
import { Text, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import IncidentStatus, { LARGE_SIZE } from '@components/incidentStatus/IncidentStatus'
import Header from '@components/header/Header'
import HeaderContent from '@components/root/HeaderContent'
import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import logger from '@utils/logger'
import api from '@api/api'

const HORIZONTAL_PADDING = 24

interface Props {
  incidentNumber: number
}

const IncidentDetailScreen = ({ incidentNumber }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [status, setStatus] = React.useState<Status>('loading')
  const [incident, setIncident] = React.useState<IncidentResponse>()
  const [site, setSite] = React.useState<SiteResponse>()
  const [manifestation, setManifestation] = React.useState<ManifestationResponse>()

  React.useEffect(() => {
    api.incident
      .getExpo(incidentNumber)
      .then((res) => {
        setIncident(res)
        return Promise.resolve(res)
      })
      .then((resIncident) => {
        if (resIncident.manifestationId) {
          return api.site.get(resIncident.siteId).then((resSite) => {
            setSite(resSite)
            return Promise.resolve({ siteId: resSite.id, manifestationId: resIncident.manifestationId })
          })
        }
        return Promise.reject()
      })
      .then(({ siteId, manifestationId }) =>
        api.manifestation.get(siteId, manifestationId || '').then((resManif) => {
          setManifestation(resManif)
        })
      )
      .then(() => setStatus('fetched'))
      .catch((err) => {
        logger.error(err)
        setStatus('error')
      })
  }, [])

  const onBack = () => navigation.push('/login')

  if (!incident) {
    return (
      <Root loading={status === 'loading'} fixed>
        {isPhone && (
          <HeaderContent
            onLeft={onBack}
            title={i18n.t('screens.incidentDetail.incidentNumber', { incidentNumber: incidentNumber })}
          />
        )}
        <MainContainer>
          {!isPhone && (
            <Header
              title={i18n.t('screens.incidentDetail.incidentNumber', { incidentNumber: incidentNumber })}
              buttons={[]}
              links={[{ label: i18n.t('screens.home.home'), onClick: onBack }]}
              color="primary"
            />
          )}
          <LeftContainer>
            <InfoContainer>
              <Info>{i18n.t('screens.incidentExpo.unknownIncident')}</Info>
            </InfoContainer>
          </LeftContainer>
        </MainContainer>
      </Root>
    )
  }

  const renderDates = () => (
    <>
      <IncidentDate onPrimary>
        {i18n.t('screens.incidentDetail.creationDate', { date: new Date(incident.dateDeCreation) })}
      </IncidentDate>

      {!!incident.datePriseEnCharge && (
        <IncidentDate onPrimary>
          {i18n.t('screens.incidentDetail.acceptedDate', { date: new Date(incident.datePriseEnCharge) })}
        </IncidentDate>
      )}
    </>
  )

  const renderPriorityTag = () => {
    if (incident.isPrioritaire && incident.status === 'EN_ATTENTE_DE_PRISE_EN_CHARGE') {
      return (
        <HighPriorityContainer>
          <Icon name="problem" size={24} color={theme.colors.alert} />
          <HighPriorityLabel>{i18n.t(`screens.incidentDetail.highPriority`)}</HighPriorityLabel>
        </HighPriorityContainer>
      )
    }
    return null
  }

  const renderBottom = () => (
    <HeaderBottom>
      {renderDates()}
      <StatusContainer>
        {renderPriorityTag()}
        <IncidentStatus incidentStatus={incident.status} size="large" />
      </StatusContainer>
    </HeaderBottom>
  )

  return (
    <Root loading={status === 'loading'} fixed>
      {isPhone && (
        <HeaderContent
          onLeft={onBack}
          title={i18n.t('screens.incidentDetail.incidentNumber', { incidentNumber: incident.numero })}
        />
      )}
      <MainContainer>
        {isPhone ? (
          <MobileHeader>
            {renderDates()}
            {renderPriorityTag()}
            <IncidentStatus incidentStatus={incident.status} size="large" />
          </MobileHeader>
        ) : (
          <Header
            title={i18n.t('screens.incidentDetail.incidentNumber', { incidentNumber: incident.numero })}
            buttons={[]}
            links={[{ label: i18n.t('screens.home.home'), onClick: onBack }]}
            renderBottom={renderBottom}
            color="primary"
          />
        )}
        <LeftContainer>
          <InfoTitle>
            <IconContainer>
              <Icon name="viparis" size={24} color={theme.colors.primary} />
            </IconContainer>
            <Title>{i18n.t('screens.incidentExpo.manifestation')}</Title>
          </InfoTitle>

          <InfoContainer>
            <Info>{i18n.t(site ? `screens.site.sites.${site.codeLieu}` : 'screens.incidentExpo.unknownSite')}</Info>
            <Info>{manifestation?.libelle || i18n.t('screens.incidentExpo.unknownManifestation')}</Info>
          </InfoContainer>

          <Separator />

          <InfoTitle>
            <IconContainer>
              <Icon name="tab_profile" size={24} color={theme.colors.primary} />
            </IconContainer>
            <Title>{i18n.t('screens.incidentDetail.exhibitor')}</Title>
          </InfoTitle>

          <InfoContainer>
            <Info>{incident.commandeAssociee?.enseigne || i18n.t('screens.incidentExpo.unknownExhibitor')}</Info>

            <Info>
              {!!incident.commandeAssociee?.espace &&
              !!incident.commandeAssociee?.allee &&
              !!incident.commandeAssociee?.stand
                ? `${incident.commandeAssociee.espace} - ${incident.commandeAssociee.allee} - ${incident.commandeAssociee.stand}`
                : i18n.t('screens.incidentExpo.unknownLocation')}
            </Info>
          </InfoContainer>

          <Separator />

          <InfoTitle>
            <IconContainer>
              <Icon name="hammer" size={24} color={theme.colors.primary} />
            </IconContainer>
            <Title>{i18n.t('screens.incidentDetail.prestation')}</Title>
          </InfoTitle>

          <InfoContainer>
            <Info>{incident.commandeAssociee?.libelleFamille || i18n.t('screens.incidentExpo.unknownFamille')}</Info>
            <Info>
              {incident.commandeAssociee?.libelleSousFamille || i18n.t('screens.incidentExpo.unknownSousFamille')}
            </Info>
            <Info>{incident.commandeAssociee?.libelleArticle || i18n.t('screens.incidentExpo.unknownArticle')}</Info>
          </InfoContainer>
          <Separator />
        </LeftContainer>
      </MainContainer>
    </Root>
  )
}

export default IncidentDetailScreen

const MainContainer = styled(ScrollView)`
  flex: 1;
`

const LeftContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  ${(props) =>
    props.theme.breakpoint === 'phone' &&
    `
    margin: 16px;
    border-radius: 8px;
    `}
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px 0 24px 0px` : `${24 + LARGE_SIZE / 2}px 0 24px`)};
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  flex: 1;
  font-size: 18px;
`

const InfoTitle = styled(View)`
  padding: 8px ${HORIZONTAL_PADDING}px;
  flex-direction: row;
  align-items: center;
`

const IconContainer = styled(View)`
  margin-right: 8px;
`

const Separator = styled(View)`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 1)}px;
  background-color: ${(props) => props.theme.colors.disable};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px ${HORIZONTAL_PADDING}px;
`

const IncidentDate = styled(Text)<{ onPrimary?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Footnote : props.theme.fonts.Body)};

  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      text-align: center;
      color: ${props.theme.colors.background};
      margin-bottom: 16px;`
      : `
      color: ${props.theme.colors.secondaryText};
      padding-top: 16px;`}

  ${(props) => props.onPrimary && `color: ${props.theme.colors.background};`}
`

const HeaderBottom = styled(View)`
  margin-bottom: -${16 + LARGE_SIZE / 2}px;
`

const StatusContainer = styled(View)`
  margin-top: 22px;
`

const InfoContainer = styled(View)`
  padding: 8px ${HORIZONTAL_PADDING}px;
`

const Info = styled(Text)`
  margin-bottom: 8px;
  ${(props) => props.theme.fonts.Footnote};
  font-size: 16px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const MobileHeader = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  padding: 16px 16px 64px;
  margin-bottom: -56px;
`

const HighPriorityContainer = styled(View)`
  height: ${LARGE_SIZE}px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.priority};
  padding: 4px 16px 12px;
  flex-direction: row;
  align-items: center;
  margin-bottom: -12px;
`

const HighPriorityLabel = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  font-size: 16px;
  margin-left: 14px;
`
